"use client";
import React, { useRef, useState } from "react";
import { useSearchParams } from "next/navigation";
import MainLayout from "@/components/layouts/main-layout";
import Image from "next/image";
import { Button } from "@/components/ui/button";
import { Link } from "@/i18n/navigation";
import ScrollableContent from "@/components/ui/scrollable-content";
import FadeIn from "@/components/ui/fade-in";
import AnimatedCard from "@/components/ui/animated-card";
import BorderWrapper from "@/components/ui/border-wrapper";
import CaseStudyVideo from "@/components/ui/case-study-video";
import { motion } from "motion/react";
import { ChevronRight, ArrowLeft, ArrowRight } from "lucide-react";
import { useTranslations } from "next-intl";
import ExternalLink from "@/components/modals/external-link";

const VisitSiteButton = ({ label }: { label: string }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        className="rounded-[10px] text-[12px] lg:text-[14px] font-normal h-[37px] lg:h-[42px] min-w-[100px] lg:min-w-[120px] cursor-pointer"
        variant="theme"
        onClick={() => setOpen(true)}
      >
        {label}
      </Button>
      <ExternalLink
        open={open}
        onOpenChange={setOpen}
        websiteUrl="https://internationaltoolindustries.com/"
        websiteLabel="ITI"
        appStoreUrl="https://apps.apple.com/nl/app/iti-shop/id6749904342?l=en-GB"
        playStoreUrl="https://play.google.com/store/apps/details?id=com.internationaltoolindustries.shop"
      />
    </>
  );
};

const shapeVariants = {
  visible: {
    scale: [0.9, 1, 0.9] as number[],
    transition: { duration: 5, ease: "easeInOut", repeat: Infinity },
  },
} as const;

const containerVariants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.1 } },
} as const;

const itemVariants = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
} as const;

const ITICaseStudyContent = () => {
  const backgroundImageRef = useRef<HTMLImageElement>(null);
  const searchParams = useSearchParams();
  const isEmbed = searchParams.get("embed") === "1";
  const t = useTranslations("caseStudies");
  const cs = useTranslations("caseStudy");
  const tA11y = useTranslations("accessibility");

  const highlights = [
    { value: t("iti.highlights.0.value"), label: t("iti.highlights.0.label") },
    { value: t("iti.highlights.1.value"), label: t("iti.highlights.1.label") },
    { value: t("iti.highlights.2.value"), label: t("iti.highlights.2.label") },
    { value: t("iti.highlights.3.value"), label: t("iti.highlights.3.label") },
    { value: t("iti.highlights.4.value"), label: t("iti.highlights.4.label") },
    { value: t("iti.highlights.5.value"), label: t("iti.highlights.5.label") },
  ];

  return (
    <MainLayout>
      <div className={`${isEmbed ? 'pt-[20px] md:pt-[30px]' : 'pt-[110px] md:pt-[140px] lg:pt-[180px]'} relative`}>
        {/* START BACKGROUND GRADIENT */}
        <div className="absolute top-0 left-0 right-0 h-[680px] z-0">
          <span className="absolute inline-block w-[514px] h-[514px] bg-[linear-gradient(180deg,_#A9A8BF_0%,_#060519_100%)] opacity-25 blur-[175px] z-2 rounded-full top-[-15%] start-[-10%]"></span>
          <Image
            ref={backgroundImageRef}
            src="/assets/backgrounds/case-style-bg-head.webp"
            width={1440}
            height={701}
            loading="eager"
            sizes="100vw"
            className="absolute inset-0 w-full h-full object-cover object-[0%_center]"
            alt="Page header background"
            aria-hidden="true"
          />
          <span className="absolute z-0 w-full h-full bg-[#6A4A8F] opacity-75 mix-blend-multiply"></span>
          <span className="absolute z-1 w-full h-full bg-[linear-gradient(180deg,_rgba(10,0,31,0.1)_0%,_rgba(10,0,31,0.6)_50%,_#0A001F_100%)]"></span>
        </div>
        {/* END BACKGROUND GRADIENT */}

        <article className="relative z-1">
          <div className="container">
            {/* BREADCRUMB */}
            {!isEmbed && <FadeIn direction="down" duration={0.4} className="mb-6">
              <nav aria-label={tA11y("breadcrumb")} className="flex items-center flex-wrap gap-y-1 gap-x-2 text-[13px] lg:text-[14px] text-foreground/40">
                <Link href="/" className="hover:text-theme transition-colors duration-300">
                  {cs("home")}
                </Link>
                <ChevronRight className="w-3.5 h-3.5" />
                <span className="text-foreground/40">
                  {cs("breadcrumbCaseStudies")}
                </span>
                <ChevronRight className="w-3.5 h-3.5" />
                <span className="text-foreground/90">{t("iti.card.title")}</span>
              </nav>
            </FadeIn>}

            {/* HEADING */}
            <div className="space-y-3 md:space-y-4 mb-[24px] md:mb-[32px] lg:mb-[40px]">
              <FadeIn delay={0.1}>
                <h1 className="max-w-[810px] text-[30px] sm:text-[38px] md:text-[55px] xl:text-[70px] font-semibold leading-[1.2]">
                  {t("iti.title")}
                </h1>
              </FadeIn>
              <FadeIn delay={0.2}>
                <p className="text-[16px] lg:text-[18px] font-light leading-[1.7] text-foreground/90 max-w-[610px]">
                  {t("iti.description")}
                </p>
              </FadeIn>
            </div>

            <FadeIn delay={0.3} scale={0.98}>
              <figure className="mb-[24px] lg:mb-[42px] relative group overflow-hidden rounded-[25px]">
                <Image
                  src={"/assets/case-studies/iti/featured.webp"}
                  alt="International Tool Industries e-commerce platform"
                  width={1215}
                  height={597}
                  priority
                  className="rounded-[25px]"
                />
              </figure>
            </FadeIn>
          </div>

          {/* Smooth fade between sections */}
          <div className="w-full h-[20px] md:h-[70px] lg:h-[100px] bg-gradient-to-b from-transparent to-[rgba(10,0,31,0.02)]"></div>

          {/* single item */}
          <div className="py-6 lg:py-10 relative">
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[rgba(10,0,31,0.02)] to-[rgba(10,0,31,0.05)] z-0"></div>
            <div className="container relative z-1">
              <div className="grid md:grid-cols-[minmax(0,1fr)_320px] lg:grid-cols-[1fr_488px] gap-[20px] md:gap-[24px] lg:gap-[30px]">
                <div className="grid__item order-2 md:order-1 relative">
                  {/* START SCROLLABLE CONTENT */}
                  <FadeIn delay={0.1} direction="right" mobileDirection="up">
                  <ScrollableContent
                    height={500}
                    disableBelow={768}
                    aria-label={t("iti.section1.heading")}
                    className="max-w-[560px] md:max-w-none"
                    contentClassName="space-y-4 text-[15px] lg:text-[16px] leading-[1.7] text-foreground/90"
                  >
                    <h2 className="text-[28px] md:text-[40px] lg:text-[48px] font-semibold leading-[1.1]">
                      {t("iti.section1.heading")}
                    </h2>
                    <p>
                      {t("iti.section1.content.0")}
                    </p>
                    <h3 className="text-[18px] lg:text-[22px] font-semibold leading-[1.2] pt-2">{t("iti.section1.subheadings.plugins")}</h3>
                    <p>
                      {t("iti.section1.subheadings.pluginsContent.0")}
                    </p>
                    <p>
                      {t("iti.section1.subheadings.pluginsContent.1")}
                    </p>
                    <h3 className="text-[18px] lg:text-[22px] font-semibold leading-[1.2] pt-2">{t("iti.section1.subheadings.vision")}</h3>
                    <p>
                      {t("iti.section1.subheadings.visionContent.0")}
                    </p>
                    <p>
                      {t("iti.section1.subheadings.visionContent.1")}
                    </p>
                    <h3 className="text-[18px] lg:text-[22px] font-semibold leading-[1.2] pt-2">{t("iti.section1.subheadings.partnership")}</h3>
                    <p>
                      {t("iti.section1.subheadings.partnershipContent.0")}
                    </p>
                    <p>
                      {t("iti.section1.subheadings.partnershipContent.1")}
                    </p>
                    <p>
                      {t("iti.section1.subheadings.partnershipContent.2")}
                    </p>
                    </ScrollableContent>
                  </FadeIn>
                  {/* END SCROLLABLE CONTENT */}
                </div>

                <div className="grid__item order-1 md:order-2 md:sticky md:top-[120px] lg:top-[140px] md:self-start relative z-10">
                  <FadeIn delay={0.2} direction="left" mobileDirection="none">
                    <AnimatedCard
                      className="rounded-[18px] lg:rounded-[22px]"
                      circleClassName="w-[150px] h-[150px] blur-[90px]"
                    >
                      <BorderWrapper
                        wrapperClass="rounded-[18px] lg:rounded-[22px]"
                        className="rounded-[18px] lg:rounded-[22px] bg-gradient-to-b from-[#121124] to-[#0e0821] p-[24px] lg:p-[36px] relative overflow-hidden"
                      >
                        <motion.div
                          className="absolute top-0 right-0 w-[200px] h-[200px] bg-theme/[0.08] rounded-full blur-[80px] pointer-events-none"
                          variants={shapeVariants}
                          initial="visible"
                          animate="visible"
                        />
                        <div className="relative z-10">
                          <div className="flex items-start justify-between mb-[24px]">
                            <div className="w-[36px] h-[2px] rounded-full bg-gradient-to-r from-theme to-[#622aad] mt-[10px]" />
                            <VisitSiteButton label={t("iti.meta.visitSite")} />
                          </div>
                          <ul className="space-y-[20px]">
                            <li>
                              <p className="text-[12px] font-light text-foreground/50 tracking-[0.12em] uppercase mb-[6px]">
                                {cs("industry")}
                              </p>
                              <p className="text-[16px] lg:text-[20px] font-semibold leading-[1.3]">
                                {t("iti.meta.industry")}
                              </p>
                            </li>
                            <li className="border-t border-white/[0.06] pt-[20px]">
                              <p className="text-[12px] font-light text-foreground/50 tracking-[0.12em] uppercase mb-[6px]">
                                {cs("location")}
                              </p>
                              <p className="text-[16px] lg:text-[20px] font-semibold leading-[1.3]">
                                {t("iti.meta.location")}
                              </p>
                            </li>
                            <li className="border-t border-white/[0.06] pt-[20px]">
                              <p className="text-[12px] font-light text-foreground/50 tracking-[0.12em] uppercase mb-[6px]">
                                {cs("stack")}
                              </p>
                              <p className="text-[16px] lg:text-[20px] font-semibold leading-[1.3]">
                                {t("iti.meta.stack")}
                              </p>
                            </li>
                            <li className="border-t border-white/[0.06] pt-[20px]">
                              <p className="text-[12px] font-light text-foreground/50 tracking-[0.12em] uppercase mb-[6px]">
                                {cs("scopeOfWork")}
                              </p>
                              <ul className="text-[15px] lg:text-[16px] font-medium leading-[1.6] space-y-[4px]">
                                <li>{t("iti.meta.scopeItems.0")}</li>
                                <li>{t("iti.meta.scopeItems.1")}</li>
                                <li>{t("iti.meta.scopeItems.2")}</li>
                                <li>{t("iti.meta.scopeItems.3")}</li>
                              </ul>
                            </li>
                          </ul>
                        </div>
                      </BorderWrapper>
                    </AnimatedCard>
                  </FadeIn>
                </div>
              </div>
            </div>

          </div>

          {/* Smooth transition between sections */}
          <div className="w-full h-[20px] md:h-[36px] lg:h-[50px] bg-transparent"></div>

          {/* single item */}
          <div className="py-6 lg:py-10 relative">
            <div className="absolute inset-0 bg-gradient-to-b from-[rgba(10,0,31,0.05)] via-[rgba(10,0,31,0.08)] to-[rgba(10,0,31,0.12)] z-0"></div>
            <div className="container relative z-1">
              <FadeIn scale={0.97}>
                <figure className="mb-[24px] lg:mb-[42px] relative overflow-hidden rounded-[25px]">
                  <Image
                    src={"/assets/case-studies/iti/banner-5.webp"}
                    alt="ITI product showcase"
                    width={1215}
                    height={597}
                    className="rounded-[25px]"
                  />
                </figure>
              </FadeIn>

              <div className="grid md:grid-cols-[minmax(0,1fr)_320px] lg:grid-cols-[1fr_488px] gap-[24px] lg:gap-[30px]">
                <div className="grid__item">
                  {/* START SCROLLABLE CONTENT */}
                  <FadeIn delay={0.1}>
                  <ScrollableContent
                    height={500}
                    disableBelow={768}
                    aria-label={t("iti.section2.heading")}
                    className="max-w-[560px] md:max-w-none"
                    contentClassName="space-y-4 text-[15px] lg:text-[16px] leading-[1.7] text-foreground/90"
                  >
                    <h2 className="text-[28px] md:text-[40px] lg:text-[48px] font-semibold leading-[1.1]">
                      {t("iti.section2.heading")}
                    </h2>
                    <p>
                      {t("iti.section2.content.0")}
                    </p>
                    <h3 className="text-[18px] lg:text-[22px] font-semibold leading-[1.2] pt-2">{t("iti.section2.subheadings.data")}</h3>
                    <p>
                      {t("iti.section2.subheadings.dataContent.0")}
                    </p>
                    <h3 className="text-[18px] lg:text-[22px] font-semibold leading-[1.2] pt-2">{t("iti.section2.subheadings.search")}</h3>
                    <p>
                      {t("iti.section2.subheadings.searchContent.0")}
                    </p>
                    <h3 className="text-[18px] lg:text-[22px] font-semibold leading-[1.2] pt-2">{t("iti.section2.subheadings.mam")}</h3>
                    <p>
                      {t("iti.section2.subheadings.mamContent.0")}
                    </p>
                    <p>
                      {t("iti.section2.subheadings.mamContent.1")}
                    </p>
                    <h3 className="text-[18px] lg:text-[22px] font-semibold leading-[1.2] pt-2">{t("iti.section2.subheadings.security")}</h3>
                    <p>
                      {t("iti.section2.subheadings.securityContent.0")}
                    </p>
                    <p>
                      {t("iti.section2.subheadings.securityContent.1")}
                    </p>
                    </ScrollableContent>
                  </FadeIn>
                  {/* END SCROLLABLE CONTENT */}
                </div>
              </div>
            </div>

            {/* CIRCLE BLUR */}
            <div
              className="absolute w-[480px] max-w-[45vw] aspect-square top-2/4 -start-[5vw] -translate-y-2/4 z-[-1] blur-[700px] rounded-full bg-theme parallax-anim"
              data-from="-5"
              data-to="5"
            ></div>
          </div>

          {/* Smooth transition between sections */}
          <div className="w-full h-[20px] md:h-[36px] lg:h-[50px] bg-transparent"></div>

          {/* single item */}
          <div className="py-6 lg:py-10 relative">
            <div className="absolute inset-0 bg-gradient-to-b from-[rgba(10,0,31,0.12)] via-[rgba(10,0,31,0.15)] to-[rgba(10,0,31,0.18)] z-0"></div>
            <div className="container relative z-1">
              <div className="mb-[24px] lg:mb-[42px] grid sm:grid-cols-2 gap-[15px] md:gap-[32px]">
                <FadeIn delay={0} scale={0.97}>
                  <figure className="grid__item relative overflow-hidden rounded-[25px]">
                    <div className="relative aspect-square w-full rounded-[25px] overflow-hidden">
                      <Image
                        src={"/assets/case-studies/iti/banner-3.webp"}
                        alt="ITI platform dashboard"
                        width={1184}
                        height={1194}
                        sizes="(min-width: 640px) 50vw, 100vw"
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                    </div>
                  </figure>
                </FadeIn>
                <FadeIn delay={0.15} scale={0.97}>
                  <figure className="grid__item relative overflow-hidden rounded-[25px]">
                    <div className="relative aspect-square w-full rounded-[25px] overflow-hidden">
                      <Image
                        src={"/assets/case-studies/iti/banner-4.webp"}
                        alt="ITI product catalogue"
                        width={1184}
                        height={1194}
                        sizes="(min-width: 640px) 50vw, 100vw"
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                    </div>
                  </figure>
                </FadeIn>
              </div>

              <div className="grid md:grid-cols-[minmax(0,1fr)_320px] lg:grid-cols-[1fr_488px] gap-[24px] lg:gap-[30px]">
                <div className="grid__item">
                  {/* START SCROLLABLE CONTENT */}
                  <FadeIn delay={0.1}>
                  <ScrollableContent
                    height={500}
                    disableBelow={768}
                    aria-label={t("iti.section3.heading")}
                    className="max-w-[560px] md:max-w-none"
                    contentClassName="space-y-4 text-[15px] lg:text-[16px] leading-[1.7] text-foreground/90"
                  >
                    <h2 className="text-[28px] md:text-[40px] lg:text-[48px] font-semibold leading-[1.1]">
                      {t("iti.section3.heading")}
                    </h2>
                    <p>
                      {t("iti.section3.content.0")}
                    </p>
                    <h3 className="text-[18px] lg:text-[22px] font-semibold leading-[1.2] pt-2">{t("iti.section3.subheadings.b2b")}</h3>
                    <p>
                      {t("iti.section3.subheadings.b2bContent.0")}
                    </p>
                    <h3 className="text-[18px] lg:text-[22px] font-semibold leading-[1.2] pt-2">{t("iti.section3.subheadings.admin")}</h3>
                    <p>
                      {t("iti.section3.subheadings.adminContent.0")}
                    </p>
                    <p>
                      {t("iti.section3.subheadings.adminContent.1")}
                    </p>
                    <h3 className="text-[18px] lg:text-[22px] font-semibold leading-[1.2] pt-2">{t("iti.section3.subheadings.dashboard")}</h3>
                    <p>
                      {t("iti.section3.subheadings.dashboardContent.0")}
                    </p>
                    <p>
                      {t("iti.section3.subheadings.dashboardContent.1")}
                    </p>
                    <h3 className="text-[18px] lg:text-[22px] font-semibold leading-[1.2] pt-2">{t("iti.section3.subheadings.native")}</h3>
                    <p>
                      {t("iti.section3.subheadings.nativeContent.0")}
                    </p>
                    <p>
                      {t("iti.section3.subheadings.nativeContent.1")}
                    </p>
                    </ScrollableContent>
                  </FadeIn>
                  {/* END SCROLLABLE CONTENT */}
                </div>
              </div>
            </div>

            {/* CIRCLE BLUR */}
            <div
              className="absolute w-[480px] max-w-[45vw] aspect-square top-2/4 -end-[5vw] -translate-y-2/4 z-[-1] blur-[700px] rounded-full bg-theme parallax-anim"
              data-from="-5"
              data-to="5"
            ></div>
          </div>

          {/* Smooth transition between sections */}
          <div className="w-full h-[20px] md:h-[36px] lg:h-[50px] bg-transparent"></div>

          {/* single item — final visuals + summary */}
          <div className="py-6 lg:py-10 relative">
            <div className="absolute inset-0 bg-gradient-to-b from-[rgba(10,0,31,0.18)] via-[rgba(10,0,31,0.22)] to-[rgba(10,0,31,0.25)] z-0"></div>
            <div className="container relative z-1">
              <div className="mb-[24px] lg:mb-[42px] space-y-[15px] lg:space-y-[20px]">
                <CaseStudyVideo
                  src="/assets/case-studies/iti/full-web.png"
                  poster="/assets/case-studies/iti/poster.webp"
                  posterMobile="/assets/case-studies/iti/poster-mobile.webp"
                  alt="International Tool Industries full website preview"
                  width={2432}
                  height={10847}
                  durationSeconds={64}
                />
                <FadeIn scale={0.97} delay={0.15}>
                  <figure className="relative overflow-hidden rounded-[25px]">
                    <Image
                      src={"/assets/case-studies/iti/banner-6.webp"}
                      alt="ITI website overview"
                      width={1215}
                      height={597}
                      className="rounded-[25px]"
                    />
                  </figure>
                </FadeIn>
              </div>

              {/* Mobile mockup section */}
              <FadeIn delay={0.1}>
                <div className="mb-[24px] lg:mb-[42px]">
                  <AnimatedCard
                    className="rounded-[20px] lg:rounded-[24px]"
                    circleClassName="w-[180px] h-[180px] blur-[100px]"
                  >
                    <BorderWrapper
                      wrapperClass="rounded-[20px] lg:rounded-[24px]"
                      className="rounded-[20px] lg:rounded-[24px] bg-gradient-to-b from-[#121124] to-[#0e0821] pt-[32px] lg:pt-[50px] pb-[32px] lg:pb-[50px] relative overflow-hidden"
                    >
                      <motion.div
                        className="absolute top-[10%] right-[15%] w-[250px] h-[250px] bg-theme/[0.07] rounded-full blur-[80px] pointer-events-none"
                        variants={shapeVariants}
                        initial="visible"
                        animate="visible"
                      />

                      <div className="relative z-10">
                        {/* Header */}
                        <div className="text-center mb-[28px] lg:mb-[44px] px-[24px] lg:px-[50px]">
                          <h3 className="text-[24px] md:text-[32px] lg:text-[40px] font-semibold leading-[1.15] mb-[10px]">
                            {cs("optimisedForEveryScreen")}
                          </h3>
                          <p className="text-[15px] lg:text-[16px] font-light leading-[1.7] text-foreground/90 max-w-[460px] mx-auto">
                            {cs("mobileDescription")}
                          </p>
                        </div>

                        {/* Phone screens */}
                        <div className="flex justify-center items-end gap-[16px] md:gap-[30px] lg:gap-[40px] px-[16px] lg:px-[40px]">
                          {[
                            { src: "/assets/case-studies/iti/screen-1.webp", alt: "ITI mobile — product catalogue", delay: 0.1 },
                            { src: "/assets/case-studies/iti/screen-2.webp", alt: "ITI mobile — product detail", delay: 0.2 },
                            { src: "/assets/case-studies/iti/screen-3.webp", alt: "ITI mobile — checkout", delay: 0.3 },
                          ].map((phone, i) => (
                            <motion.div
                              key={i}
                              initial={{ opacity: 0, y: 40 }}
                              whileInView={{ opacity: 1, y: 0 }}
                              viewport={{ once: true, margin: "0px 0px -60px 0px" }}
                              transition={{ duration: 0.5, delay: phone.delay, ease: "easeOut" }}
                              className="relative w-[30%] max-w-[240px]"
                            >
                              <Image
                                src={phone.src}
                                alt={phone.alt}
                                width={664}
                                height={1438}
                                className="w-full h-auto rounded-[12px] sm:rounded-[16px] md:rounded-[20px] lg:rounded-[24px] shadow-[0_4px_24px_rgba(0,0,0,0.4)]"
                              />
                            </motion.div>
                          ))}
                        </div>
                      </div>
                    </BorderWrapper>
                  </AnimatedCard>
                </div>
              </FadeIn>

              {/* Summary text for final section */}
              <FadeIn delay={0.1}>
                <div className="max-w-[700px] space-y-4 text-[15px] lg:text-[16px] leading-[1.7]">
                  <h2 className="text-[28px] md:text-[40px] lg:text-[48px] font-semibold leading-[1.1]">
                    {t("iti.finalProduct.heading")}
                  </h2>
                  <p>
                    {t("iti.finalProduct.description")}
                  </p>
                </div>
              </FadeIn>
            </div>
          </div>

          {/* Smooth transition */}
          <div className="w-full h-[20px] md:h-[36px] lg:h-[50px] bg-transparent"></div>

          {/* RESULTS / METRICS SECTION */}
          <div className="py-6 md:py-10 lg:py-20 relative">
            <div className="absolute inset-0 bg-gradient-to-b from-[rgba(10,0,31,0.25)] via-[rgba(10,0,31,0.28)] to-[rgba(10,0,31,0.3)] z-0"></div>
            <motion.div
              className="absolute left-[-5%] top-[10%] w-[500px] h-[500px] bg-[linear-gradient(180deg,_rgba(135,82,250,0.1)_0%,_rgba(34,12,81,0)_100%)] rounded-full blur-[120px] pointer-events-none"
              variants={shapeVariants}
              initial="visible"
              animate="visible"
            />
            <div className="container relative z-1">
              <motion.div
                variants={containerVariants}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "0px 0px -80px 0px" }}
              >
                <motion.div variants={itemVariants} className="mb-[30px] lg:mb-[50px]">
                  <h2 className="text-[28px] md:text-[40px] lg:text-[60px] font-semibold leading-[1.1] mb-[16px]">
                    {t("iti.keyResults.heading")}
                  </h2>
                  <p className="text-[15px] font-light leading-[1.7] text-foreground/85 max-w-[560px]">
                    {t("iti.keyResults.description")}
                  </p>
                </motion.div>

                <motion.div variants={itemVariants}>
                  <AnimatedCard
                    className="rounded-[20px] lg:rounded-[24px]"
                    circleClassName="w-[200px] h-[200px] blur-[100px]"
                  >
                    <BorderWrapper
                      wrapperClass="rounded-[20px] lg:rounded-[24px]"
                      className="rounded-[20px] lg:rounded-[24px] bg-gradient-to-b from-[#121124] to-[#0e0821] overflow-hidden relative"
                    >
                      <motion.div
                        className="absolute top-0 right-0 w-[300px] h-[300px] bg-theme/[0.08] rounded-full blur-[100px] pointer-events-none"
                        variants={shapeVariants}
                        initial="visible"
                        animate="visible"
                      />
                      <motion.div
                        className="absolute bottom-0 left-0 w-[250px] h-[250px] bg-[linear-gradient(180deg,_rgba(150,128,197,0.1)_0%,_rgba(34,12,81,0)_100%)] rounded-full blur-[80px] pointer-events-none"
                        variants={shapeVariants}
                        initial="visible"
                        animate="visible"
                      />
                      <div className="grid grid-cols-2 md:grid-cols-3 divide-x divide-y divide-white/[0.06]">
                        {highlights.map((item, i) => (
                          <div
                            key={i}
                            className="px-[24px] lg:px-[36px] py-[28px] lg:py-[36px] relative group"
                          >
                            <div className="text-[28px] md:text-[36px] lg:text-[48px] font-semibold text-[#4a3290] leading-[1] mb-[8px]">
                              {item.value}
                            </div>
                            <div className="text-[13px] font-light text-foreground/90">
                              {item.label}
                            </div>
                          </div>
                        ))}
                      </div>
                    </BorderWrapper>
                  </AnimatedCard>
                </motion.div>
              </motion.div>
            </div>
          </div>

          {/* Smooth transition */}
          <div className="w-full h-[20px] lg:h-[50px] bg-transparent"></div>

          {/* TESTIMONIAL SECTION — stacked cards */}
          {process.env.NEXT_PUBLIC_SHOW_TESTIMONIAL_ITI !== "false" && (
          <div className="py-6 md:py-10 lg:py-20 relative">
            <div className="absolute inset-0 bg-gradient-to-b from-[rgba(10,0,31,0.3)] via-[rgba(10,0,31,0.32)] to-[rgba(10,0,31,0.35)] z-0"></div>
            <motion.div
              className="absolute right-[-5%] top-[20%] w-[500px] h-[500px] bg-[linear-gradient(180deg,_rgba(150,128,197,0.08)_0%,_rgba(34,12,81,0)_100%)] rounded-full blur-[100px] pointer-events-none"
              variants={shapeVariants}
              initial="visible"
              animate="visible"
            />
            <div className="container relative z-1">
              <motion.div
                variants={containerVariants}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "0px 0px -80px 0px" }}
              >
                {/* Stacked card wrapper */}
                <motion.div variants={itemVariants} className="relative pt-[12px] md:pt-[28px] lg:pt-[32px] pb-[10px] md:pb-[24px] lg:pb-[28px] px-[4px] md:px-[24px] lg:px-[30px] max-w-[900px] mx-auto">
                  {/* Bottom card (3rd layer — same size, rotated + offset) */}
                  <div
                    className="absolute inset-0 rounded-[16px] md:rounded-[20px] lg:rounded-[24px] bg-[linear-gradient(0deg,_#121124_0%,_#353353_100%)] p-[1px] z-0 hidden md:block"
                    style={{ transform: "rotate(3.5deg) translateY(-8px)" }}
                  >
                    <div className="w-full h-full rounded-[16px] md:rounded-[20px] lg:rounded-[24px] bg-gradient-to-b from-[#0a0619] to-[#080516]" />
                  </div>

                  {/* Middle card (2nd layer — same size, rotated opposite + offset) */}
                  <div
                    className="absolute inset-0 rounded-[16px] md:rounded-[20px] lg:rounded-[24px] bg-[linear-gradient(0deg,_#121124_0%,_#353353_100%)] p-[1px] z-[1] hidden md:block"
                    style={{ transform: "rotate(-2deg) translateY(-4px)" }}
                  >
                    <div className="w-full h-full rounded-[16px] md:rounded-[20px] lg:rounded-[24px] bg-gradient-to-b from-[#0e0b22] to-[#0a0619]" />
                  </div>

                  {/* Front card (main testimonial) */}
                  <div className="relative z-[2]">
                    <AnimatedCard
                      className="rounded-[16px] md:rounded-[20px] lg:rounded-[24px]"
                      circleClassName="w-[180px] h-[180px] blur-[100px]"
                    >
                      <BorderWrapper
                        wrapperClass="rounded-[16px] md:rounded-[20px] lg:rounded-[24px]"
                        className="rounded-[16px] md:rounded-[20px] lg:rounded-[24px] bg-gradient-to-b from-[#121124] to-[#0e0821] p-[24px] md:p-[32px] lg:p-[50px] relative overflow-hidden"
                      >
                        <motion.div
                          className="absolute top-0 right-0 w-[300px] h-[300px] bg-[linear-gradient(180deg,_rgba(150,128,197,0.18)_0%,_rgba(98,42,173,0.1)_50%,_rgba(34,12,81,0)_100%)] rounded-full blur-[60px] pointer-events-none"
                          variants={shapeVariants}
                          initial="visible"
                          animate="visible"
                        />

                        {/* Large quote mark */}
                        <div className="text-[80px] md:text-[140px] leading-none font-serif text-theme/15 absolute top-[6px] md:top-[10px] right-[16px] md:right-[24px] select-none">
                          &ldquo;
                        </div>

                        <div className="relative z-10 grid lg:grid-cols-[1fr_auto] gap-[24px] lg:gap-[60px] items-start">
                          <div>
                            <div className="w-[36px] h-[2px] rounded-full bg-gradient-to-r from-theme to-[#622aad] mb-[20px] md:mb-[28px]" />
                            <blockquote className="text-[18px] md:text-[22px] lg:text-[30px] font-semibold leading-[1.35] text-foreground/90">
                              {t("iti.testimonial.quote1")}
                            </blockquote>
                            <p className="mt-[16px] md:mt-[20px] text-[14px] md:text-[15px] font-light leading-[1.7] text-foreground/90">
                              {t("iti.testimonial.quote2")}
                            </p>
                          </div>
                          <div className="lg:pt-[34px] flex lg:flex-col items-center lg:items-start gap-[14px]">
                            <div className="w-[44px] h-[44px] lg:w-[52px] lg:h-[52px] rounded-full bg-theme/15 border border-theme/20 flex items-center justify-center shrink-0 p-[10px] lg:p-[12px]">
                              <img src="/assets/case-studies/iti/logo-iti.svg" alt="International Tool Industries" width={148} height={148} className="w-full h-full object-contain invert opacity-70" />
                            </div>
                            <div>
                              <p className="text-[14px] md:text-[15px] font-semibold leading-[1.3]">{t("iti.testimonial.name")}</p>
                              <p className="text-[11px] md:text-[12px] font-light text-foreground/50 tracking-[0.12em] uppercase mt-[4px]">
                                {t("iti.testimonial.role")}
                              </p>
                            </div>
                          </div>
                        </div>
                      </BorderWrapper>
                    </AnimatedCard>
                  </div>
                </motion.div>
              </motion.div>
            </div>
          </div>
          )}

          {/* CASE STUDY NAVIGATION */}
          {!isEmbed && <div className="py-6 lg:py-10 mb-[40px] md:mb-[80px] lg:mb-[140px] relative">
            <div className="absolute inset-0 bg-gradient-to-b from-[rgba(10,0,31,0.35)] to-[rgba(10,0,31,0.4)] z-0"></div>
            <div className="container relative z-1 px-[24px] lg:px-[15px]">
              <FadeIn>
                <Link
                  href="/case-study/taksverden"
                  className="group flex items-center gap-4 text-foreground/40 hover:text-foreground transition-colors duration-300"
                >
                  <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform duration-300" />
                  <div>
                    <p className="text-[16px] lg:text-[20px] font-semibold text-foreground/90 group-hover:text-foreground transition-colors duration-300">
                      {cs("previousProject")}
                    </p>
                    <p className="text-[13px] font-light text-foreground/40 mt-[4px]">
                      {t("iti.nav.previous")}
                    </p>
                  </div>
                </Link>
              </FadeIn>
            </div>
          </div>}
        </article>
      </div>
    </MainLayout>
  );
};

export default ITICaseStudyContent;
